/* eslint-disable security/detect-object-injection */
import { useInteractiveMap } from '@bees-web/core/shared/contexts'
import { IBlock } from '@bees-web/core/types'
import { FC, createElement, memo, useCallback, useEffect, useMemo } from 'react'

import { mergeProperties } from '../../utils/mergeProperties'
import { useComponent, useOptimizedStore } from '../hooks'

// eslint-disable-next-line import/no-cycle
import DeferredBlock from './DeferredBlock'
import { ErrorBoundary } from './ErrorBoundary'

export interface BlockProps {
  template: IBlock
}

export const CUSTOM_BLOCK_TAG = 'RenderComponent'

// Lazy load sitemap para reduzir bundle inicial
const getSitemap = () => import('../../sitemap/sitemap.json').then((m) => m.default)

// Memoizar função setBlockMeta para evitar recriação
const setBlockMeta = (block: IBlock, sitemap?: any): void => {
  if (!block.meta) block.meta = {}
  if (block.attributes?.href && sitemap) {
    const { href } = block.attributes
    const dynamicVariation = `${href.substring(0, href.lastIndexOf('/'))}/[id]`

    block.meta.externalLink =
      href === '/' || (!sitemap.routes[href] && !sitemap.routes[dynamicVariation])

    if (sitemap?.dynamicRoutes[dynamicVariation]) {
      block.meta.dynamicRoute = dynamicVariation
    }
  }
}

// Otimizar função de comparação do React.memo
const arePropsEqual = (prevProps: BlockProps, nextProps: BlockProps): boolean => {
  const prev = prevProps.template
  const next = nextProps.template

  // Comparação otimizada - verificar propriedades importantes primeiro
  if (prev === next) return true
  if (prev.blockType !== next.blockType) return false
  if (prev.name !== next.name) return false
  if (prev.interactiveId !== next.interactiveId) return false
  if (prev.deferred !== next.deferred) return false

  // Comparar referência do array de blocks: quando o conteúdo do bloco
  // vem de fonte dinâmica (ex.: SWR/deferred), a length pode permanecer
  // igual mas as referências dos children mudam — usar ref garante que
  // novos dados do servidor disparem re-render corretamente.
  if (prev.blocks !== next.blocks) return false

  // Comparar referência de `meta`: blocos sem children (ex.: Wrappers
  // de side-effect com `children={null}`) só recebem novos dados via
  // `meta` (resolvido pelo server processor). Sem este check, o memo
  // bail-out impede `useEffect([meta])` de disparar `onDeferredLoad`
  // — mantendo o estado Redux (filtros, etc.) defasado em cache hits.
  if (prev.meta !== next.meta) return false

  // Comparação superficial de attributes (evitar deep comparison)
  const prevAttrs = prev.attributes || {}
  const nextAttrs = next.attributes || {}
  const prevKeys = Object.keys(prevAttrs)
  const nextKeys = Object.keys(nextAttrs)

  if (prevKeys.length !== nextKeys.length) return false

  for (const key of prevKeys) {
    if (prevAttrs[key] !== nextAttrs[key]) return false
  }

  /* TODO: Optimize this
  for (let idx = 0; idx < prev.blocks?.length; idx++) {
    if (
      !arePropsEqual(
        { template: prev.blocks[idx] },
        { template: next.blocks[idx] }
      )
    )
      return false
  }
  */

  return true
}

const Block: FC<BlockProps> = memo(({ template }: BlockProps) => {
  const isCustomBlock = template.blockType === CUSTOM_BLOCK_TAG
  const store = useOptimizedStore(isCustomBlock)
  const Component = useComponent(
    isCustomBlock ? template.name : template.blockType,
    isCustomBlock
  )

  const interactiveMap = useInteractiveMap()

  const interactive = useMemo(() => {
    if (!template.interactiveId) return undefined
    return interactiveMap?.[template.interactiveId]
  }, [template.interactiveId, interactiveMap])

  // Otimizar criação de props
  const props = useMemo(() => {
    const { interactiveId, blocks: _blocks, deferred: _deferred, ...rest } = template
    const interactiveData = interactiveId ? interactive || {} : {}
    return {
      ...mergeProperties(rest, interactiveData),
      key: rest.name,
      store,
    }
  }, [template, interactive, store])

  // Lazy load sitemap apenas quando necessário
  useEffect(() => {
    if (template.attributes?.href) {
      getSitemap()
        .then((sitemap) => {
          setBlockMeta(template, sitemap)
        })
        .catch(() => {
          // Fallback sem sitemap
          setBlockMeta(template)
        })
    }
  }, [template])

  const finalProps = useMemo(() => {
    return props.attributes ? { ...props, ...props.attributes } : props
  }, [props])

  // Otimizar renderBlocks
  const renderBlocks = useCallback(
    (blocks: IBlock[]) => {
      if (!blocks?.length) return null

      return blocks.map((block, idx) => {
        const key = `${
          block.name || `${template.name || template.blockType}-${block.blockType}`
        }-${idx}`

        return (
          <ErrorBoundary blockName={block.name || ''} key={`error-${key}`}>
            <Block key={key} template={block} />
          </ErrorBoundary>
        )
      })
    },
    [template.blockType, template.name] // Dependências necessárias
  )

  if (!Component) return null
  if (template.interactiveId && !interactive) return null
  if (template.deferred) return <DeferredBlock template={template} />

  try {
    return createElement(Component, finalProps, renderBlocks(template.blocks || []))
  } catch (error) {
    console.error('Block render error:', error, template.blockType)
    return <>{`Error: ${template.blockType}`}</>
  }
}, arePropsEqual)

Block.displayName = 'BlockOptimized'

export default Block
